import React, { useState, useRef, useEffect } from "react";
import { MapContainer, TileLayer, Polygon, Circle, Popup, FeatureGroup, LayersControl, Marker, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import { EditControl } from "react-leaflet-draw";
import axios from "axios";
import L from "leaflet";
import {
  FaSeedling,
  FaCloudRain,
  FaTemperatureHigh,
  FaTint,
  FaMapMarkerAlt,
  FaFlask,
  FaLeaf,
  FaLightbulb,
  FaPencilAlt
} from "react-icons/fa";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, PieChart, Pie, Cell, ResponsiveContainer, LineChart, Line } from "recharts";
import Detailspdf from './Detailspdf';
import './CropAdvisory.css';

// Fix Leaflet icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.3/dist/images/marker-shadow.png",
});

// Custom Freehand Drawing Component
const FreehandDrawing = ({ onShapeCreated, featureGroupRef }) => {
  const map = useMap();
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPath, setCurrentPath] = useState([]);
  const [freehandMode, setFreehandMode] = useState(false);
  const currentPolylineRef = useRef(null);

  useEffect(() => {
    if (!map) return;

    let drawing = false;
    let points = [];
    let tempPolyline = null;

    // ── Helper: convert a DOM event (mouse or touch) to a Leaflet LatLng ──
    const eventToLatLng = (e) => {
      // Touch event
      if (e.touches && e.touches.length > 0) {
        const touch = e.touches[0];
        const containerPoint = map.mouseEventToContainerPoint({
          clientX: touch.clientX,
          clientY: touch.clientY
        });
        return map.containerPointToLatLng(containerPoint);
      }
      // Mouse event (already wrapped by Leaflet)
      if (e.latlng) return e.latlng;
      // Raw mouse event
      const containerPoint = map.mouseEventToContainerPoint(e);
      return map.containerPointToLatLng(containerPoint);
    };

    // ── START ──
    const startDraw = (e) => {
      if (!freehandMode) return;
      // Prevent map pan / page scroll
      if (e.originalEvent) e.originalEvent.preventDefault();
      else if (e.preventDefault) e.preventDefault();

      drawing = true;
      const latlng = eventToLatLng(e.originalEvent || e);
      if (!latlng) return;

      points = [latlng];
      setCurrentPath([latlng]);
      setIsDrawing(true);

      map.dragging.disable();
      map.touchZoom && map.touchZoom.disable();
      map.doubleClickZoom.disable();
      map.scrollWheelZoom && map.scrollWheelZoom.disable();

      tempPolyline = L.polyline([latlng], {
        color: "#9C27B0",
        weight: 3,
        opacity: 0.8
      }).addTo(map);
    };

    // ── CONTINUE ──
    const continueDraw = (e) => {
      if (!drawing || !freehandMode) return;
      if (e.originalEvent) e.originalEvent.preventDefault();
      else if (e.preventDefault) e.preventDefault();

      const latlng = eventToLatLng(e.originalEvent || e);
      if (!latlng) return;

      points.push(latlng);
      setCurrentPath([...points]);
      if (tempPolyline) tempPolyline.setLatLngs(points);
    };

    // ── FINISH ──
    const finishDraw = (e) => {
      if (!drawing || !freehandMode) {
        if (tempPolyline) { map.removeLayer(tempPolyline); tempPolyline = null; }
        drawing = false;
        setIsDrawing(false);
        map.dragging.enable();
        map.touchZoom && map.touchZoom.enable();
        map.doubleClickZoom.enable();
        map.scrollWheelZoom && map.scrollWheelZoom.enable();
        return;
      }

      drawing = false;
      setIsDrawing(false);
      map.dragging.enable();
      map.touchZoom && map.touchZoom.enable();
      map.doubleClickZoom.enable();
      map.scrollWheelZoom && map.scrollWheelZoom.enable();

      if (tempPolyline) { map.removeLayer(tempPolyline); tempPolyline = null; }

      if (points.length < 3) { points = []; setCurrentPath([]); return; }

      // Close the shape if needed
      const lastPoint = points[points.length - 1];
      const firstPoint = points[0];
      if (map.distance(lastPoint, firstPoint) > 50) points.push(firstPoint);

      const polygon = L.polygon(points, {
        color: "#9C27B0",
        weight: 2,
        fillOpacity: 0.2,
        fillColor: "#9C27B0"
      });

      if (featureGroupRef.current) featureGroupRef.current.addLayer(polygon);
      onShapeCreated && onShapeCreated(points, 'freehand');

      setCurrentPath([]);
      points = [];
    };

    // ── Native touch handlers (attached directly to the map container) ──
    // We use native DOM events for touch so we can call preventDefault()
    // which stops the browser from scrolling / zooming the page.
    const mapContainer = map.getContainer();

    const onTouchStart = (e) => {
      if (!freehandMode) return;
      e.preventDefault();
      e.stopPropagation();
      const touch = e.touches[0];
      const containerPoint = map.mouseEventToContainerPoint({
        clientX: touch.clientX,
        clientY: touch.clientY
      });
      const latlng = map.containerPointToLatLng(containerPoint);

      drawing = true;
      points = [latlng];
      setCurrentPath([latlng]);
      setIsDrawing(true);

      map.dragging.disable();
      map.touchZoom && map.touchZoom.disable();
      map.doubleClickZoom.disable();

      tempPolyline = L.polyline([latlng], {
        color: "#9C27B0",
        weight: 3,
        opacity: 0.8
      }).addTo(map);
    };

    const onTouchMove = (e) => {
      if (!drawing || !freehandMode) return;
      e.preventDefault();
      e.stopPropagation();
      const touch = e.touches[0];
      const containerPoint = map.mouseEventToContainerPoint({
        clientX: touch.clientX,
        clientY: touch.clientY
      });
      const latlng = map.containerPointToLatLng(containerPoint);

      points.push(latlng);
      setCurrentPath([...points]);
      if (tempPolyline) tempPolyline.setLatLngs(points);
    };

    const onTouchEnd = (e) => {
      if (!drawing || !freehandMode) {
        if (tempPolyline) { map.removeLayer(tempPolyline); tempPolyline = null; }
        drawing = false;
        setIsDrawing(false);
        map.dragging.enable();
        map.touchZoom && map.touchZoom.enable();
        return;
      }

      drawing = false;
      setIsDrawing(false);
      map.dragging.enable();
      map.touchZoom && map.touchZoom.enable();
      map.doubleClickZoom.enable();

      if (tempPolyline) { map.removeLayer(tempPolyline); tempPolyline = null; }

      if (points.length < 3) { points = []; setCurrentPath([]); return; }

      const lastPoint = points[points.length - 1];
      const firstPoint = points[0];
      if (map.distance(lastPoint, firstPoint) > 50) points.push(firstPoint);

      const polygon = L.polygon(points, {
        color: "#9C27B0",
        weight: 2,
        fillOpacity: 0.2,
        fillColor: "#9C27B0"
      });

      if (featureGroupRef.current) featureGroupRef.current.addLayer(polygon);
      onShapeCreated && onShapeCreated(points, 'freehand');

      setCurrentPath([]);
      points = [];
    };

    if (freehandMode) {
      // Mouse events (desktop)
      map.on('mousedown', startDraw);
      map.on('mousemove', continueDraw);
      map.on('mouseup', finishDraw);

      // Touch events (mobile) — use passive:false so preventDefault() works
      mapContainer.addEventListener('touchstart', onTouchStart, { passive: false });
      mapContainer.addEventListener('touchmove', onTouchMove, { passive: false });
      mapContainer.addEventListener('touchend', onTouchEnd, { passive: false });

      // Disable browser touch-action so the browser doesn't scroll/zoom
      // while the user is drawing on the map
      mapContainer.style.touchAction = 'none';
    }

    return () => {
      map.off('mousedown', startDraw);
      map.off('mousemove', continueDraw);
      map.off('mouseup', finishDraw);
      mapContainer.removeEventListener('touchstart', onTouchStart);
      mapContainer.removeEventListener('touchmove', onTouchMove);
      mapContainer.removeEventListener('touchend', onTouchEnd);
      if (tempPolyline) map.removeLayer(tempPolyline);
      // Always re-enable map interactions on cleanup
      map.dragging.enable();
      map.touchZoom && map.touchZoom.enable();
      map.doubleClickZoom.enable();
      map.scrollWheelZoom && map.scrollWheelZoom.enable();
      // Restore browser touch-action
      mapContainer.style.touchAction = '';
    };
  }, [map, freehandMode, onShapeCreated, featureGroupRef]);

  return (
    <div className="freehand-controls" style={{
      position: 'absolute',
      top: '10px',
      left: '10px',
      zIndex: 1000,
      background: 'white',
      padding: '8px',
      borderRadius: '6px',
      boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
      border: '2px solid #ddd'
    }}>
      <button
        onClick={() => setFreehandMode(!freehandMode)}
        style={{
          background: freehandMode ? '#9C27B0' : '#fff',
          color: freehandMode ? '#fff' : '#333',
          border: freehandMode ? '2px solid #9C27B0' : '2px solid #ccc',
          padding: '6px 12px',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          transition: 'all 0.3s ease',
          // Larger tap target on mobile
          minHeight: '40px',
          minWidth: '120px',
          touchAction: 'manipulation'
        }}
        title="Toggle Freehand Drawing Mode"
      >
        <FaPencilAlt size="16px" />
        Freehand {freehandMode ? 'ON' : 'OFF'}
      </button>
      {freehandMode && (
        <div style={{
          fontSize: '11px',
          marginTop: '6px',
          color: '#666',
          textAlign: 'center',
          fontStyle: 'italic'
        }}>
          {isDrawing
            ? 'Drawing… lift finger to finish'
            : 'Touch & drag on the map to draw'}
        </div>
      )}
    </div>
  );
};

const IndiaMap = () => {
  const [location, setLocation] = useState("");
  const [pincode, setPincode] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mapCenter, setMapCenter] = useState([20.5937, 78.9629]);
  const [zoomLevel, setZoomLevel] = useState(5);
  const [rectangleBounds, setRectangleBounds] = useState(null);
  const [customShapeBounds, setCustomShapeBounds] = useState(null);
  const [shapeType, setShapeType] = useState(null);
  const [showSteps, setShowSteps] = useState(true);
  const [soilData, setSoilData] = useState(null);
  const [locationPermissionDenied, setLocationPermissionDenied] = useState(false);
  const [groundwaterData, setGroundwaterData] = useState(null);
  const [loadingGroundwater, setLoadingGroundwater] = useState(false);

  // State coordinates for auto-zoom functionality
  const stateCoordinates = {
    'Rajasthan': [27.0238, 74.2179],
    'Gujarat': [23.0225, 72.5714],
    'Maharashtra': [19.7515, 75.7139],
    'Punjab': [31.1471, 75.3412],
    'Haryana': [29.0588, 76.0856],
    'Uttar Pradesh': [26.8467, 80.9462],
    'Madhya Pradesh': [22.9734, 78.6569],
    'Karnataka': [15.3173, 75.7139],
    'Andhra Pradesh': [15.9129, 79.7400],
    'Tamil Nadu': [11.1271, 78.6569],
    'West Bengal': [22.9868, 87.8550],
    'Bihar': [25.0961, 85.3131],
    'Odisha': [20.9517, 85.0985],
    'Kerala': [10.8505, 76.2711],
    'Assam': [26.2006, 92.9376],
    'Jharkhand': [23.6102, 85.2799],
    'Chhattisgarh': [21.2787, 81.8661],
    'Uttarakhand': [30.0668, 79.0193],
    'Himachal Pradesh': [31.1048, 77.1734],
    'Jammu and Kashmir': [34.0837, 74.7973],
    'Goa': [15.2993, 74.1240],
    'Tripura': [23.9408, 91.9882],
    'Meghalaya': [25.4670, 91.3662],
    'Manipur': [24.6637, 93.9063],
    'Mizoram': [23.1645, 92.9376],
    'Nagaland': [26.1584, 94.5624],
    'Arunachal Pradesh': [28.2180, 94.7278],
    'Sikkim': [27.5330, 88.5122],
    'Delhi': [28.7041, 77.1025],
    'Chandigarh': [30.7333, 76.7794],
    'Puducherry': [11.9416, 79.8083],
    'Andaman and Nicobar Islands': [11.7401, 92.6586],
    'Lakshadweep': [10.5667, 72.6417],
    'Dadra and Nagar Haveli': [20.1809, 73.0169],
    'Daman and Diu': [20.4283, 72.8397],
    'Ladakh': [34.1526, 77.5771],
    'Telangana': [18.1124, 79.0193]
  };

  // Check if user is from state dashboard and auto-zoom to their state
  useEffect(() => {
    const userData = localStorage.getItem('user');
    const currentPath = window.location.pathname;
    
    if (userData && (currentPath.includes('/state/crop-advisory') || currentPath.includes('/district/crop-advisory'))) {
      try {
        const user = JSON.parse(userData);
        const selectedState = user.selectedState;
        
        if (selectedState && stateCoordinates[selectedState]) {
          const stateCoords = stateCoordinates[selectedState];
          setMapCenter(stateCoords);
          setZoomLevel(7);
          setLocation(selectedState);
          
          // Auto-analyze the state
          setTimeout(() => {
            fetchAnalysis({ location: selectedState });
          }, 1000);
        }
      } catch (error) {
        console.error('Error parsing user data for state auto-zoom:', error);
      }
    }
  }, []);

  // Determine if user is state/district admin (hide input section for them)
  const isStateOrDistrictUser = () => {
    const currentPath = window.location.pathname;
    return currentPath.includes('/state/') || currentPath.includes('/district/');
  };

  const getUserLocation = () => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by your browser");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lon = position.coords.longitude;
        setLatitude(lat.toString());
        setLongitude(lon.toString());
        setLocationPermissionDenied(false);
        fetchAnalysis({ latitude: lat, longitude: lon });
      },
      (err) => {
        console.error("Error getting location:", err);
        setLocationPermissionDenied(true);
        setError("Location permission denied or unavailable");
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 }
    );
  };

  useEffect(() => {
    // Only get user location for farmers, not for state/district users
    if (!isStateOrDistrictUser()) {
      getUserLocation();
    }
  }, []);

  const featureGroupRef = useRef(null);
  const mapRef = useRef(null);

  const handleInputChange = (e) => setLocation(e.target.value);
  const handlePincodeChange = (e) => setPincode(e.target.value);
  const handleLatitudeChange = (e) => setLatitude(e.target.value);
  const handleLongitudeChange = (e) => setLongitude(e.target.value);
  const [hasDrawnShape, setHasDrawnShape] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const postData = {
      location: location || undefined,
      pincode: pincode || undefined,
      latitude: latitude ? parseFloat(latitude) : undefined,
      longitude: longitude ? parseFloat(longitude) : undefined,
    };
    await fetchAnalysis(postData);
  };

  useEffect(() => {
    if (soilData) {
      console.log("soilData updated:", soilData);
    } else {
      console.log("soilData is null or undefined");
    }
  }, [soilData]);

  // Helper function to normalize state names for matching with backend data
  const normalizeStateName = (name) => {
    if (!name) return name;
    
    // Trim and convert to uppercase for comparison
    const normalized = name.trim().toUpperCase();
    
    // Handle special cases where frontend names differ from backend
    const stateMapping = {
      'NEW DELHI': 'DELHI',
      'NCT OF DELHI': 'DELHI',
      'NATIONAL CAPITAL TERRITORY OF DELHI': 'DELHI',
      'DADRA & NAGAR HAVELI': 'DADRA AND NAGAR HAVELI',
      'DADRA AND NAGAR HAVELI AND DAMAN AND DIU': 'DADRA AND NAGAR HAVELI',
      'DAMAN & DIU': 'DAMAN AND DIU',
      'JAMMU & KASHMIR': 'JAMMU AND KASHMIR',
      'ANDAMAN & NICOBAR': 'ANDAMAN AND NICOBAR ISLANDS',
      'ANDAMAN AND NICOBAR': 'ANDAMAN AND NICOBAR ISLANDS',
      'A & N ISLANDS': 'ANDAMAN AND NICOBAR ISLANDS'
    };
    
    // Return mapped name if exists, otherwise return normalized name
    return stateMapping[normalized] || normalized;
  };

  // Fetch groundwater data based on location
  const fetchGroundwaterData = async (latitude, longitude, locationName, stateFromData, districtFromData) => {
    setLoadingGroundwater(true);

    // Declare endpoint outside try block so it's accessible in catch
    let endpoint = `${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001"))}/api/groundwater/summary`;
    let stateName = null;
    let districtName = null;

    try {
      console.log("Fetching groundwater data for:", {
        latitude,
        longitude,
        locationName,
        stateFromData,
        districtFromData
      });

      // Priority 1: Use state and district from API response data
      if (stateFromData) {
        stateName = normalizeStateName(stateFromData);
        if (districtFromData) {
          districtName = districtFromData;
        }
      }

      // Priority 2: Parse from location name if no data from API
      if (!stateName && locationName) {
        const parts = locationName.split(',').map(p => p.trim());

        if (parts.length >= 3) {
          // Format: City, District, State
          districtName = parts[1];
          stateName = normalizeStateName(parts[2]);
        } else if (parts.length === 2) {
          // Format: District, State or City, State
          districtName = parts[0];
          stateName = normalizeStateName(parts[1]);
        } else if (parts.length === 1) {
          // Single location - could be state or district
          stateName = normalizeStateName(parts[0]);
        }
      }

      console.log("Normalized state name:", stateName);

      // Build API endpoint based on available data
      if (stateName && districtName) {
        endpoint = `${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001")))}/api/groundwater/district/${encodeURIComponent(stateName)}/${encodeURIComponent(districtName)}`;
        console.log(`Trying district endpoint: ${endpoint}`);
      } else if (stateName) {
        endpoint = `${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001")))}/api/groundwater/state/${encodeURIComponent(stateName)}`;
        console.log(`Trying state endpoint: ${endpoint}`);
      } else {
        console.log(`Using summary endpoint: ${endpoint}`);
      }

      const response = await axios.get(endpoint, { timeout: 10000 });
      console.log("Groundwater data received:", response.data);
      setGroundwaterData(response.data);
    } catch (error) {
      console.error("Error fetching groundwater data:", error);

      // If district endpoint failed, try state endpoint
      if (error.response?.status === 404 && endpoint.includes('/district/')) {
        try {
          const extractedState = endpoint.split('/state/')[1]?.split('/')[0];
          if (extractedState) {
            const stateEndpoint = `${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001")))}/api/groundwater/state/${extractedState}`;
            console.log(`Retrying with state endpoint: ${stateEndpoint}`);
            const stateResponse = await axios.get(stateEndpoint, { timeout: 10000 });
            setGroundwaterData(stateResponse.data);
            return;
          }
        } catch (retryError) {
          console.error("State endpoint also failed:", retryError);
        }
      }

      // Set null data if all attempts fail
      setGroundwaterData(null);
    } finally {
      setLoadingGroundwater(false);
    }
  };

  const fetchAnalysis = async (postData) => {
    setLoading(true);
    setError(null);
    setSoilData(null);
    setGroundwaterData(null);
    try {
      console.log("Sending API request with:", postData);
      const res = await axios.post(`${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001"))}/analyze`, postData, {
        timeout: 60000,
      });
      console.log("API Response:", res.data);

      const newSoilData = {
        ndvi: res.data.ndvi !== null && res.data.ndvi !== undefined ? res.data.ndvi : null,
        evi: res.data.evi !== null && res.data.evi !== undefined ? res.data.evi : null,
        ndwi: res.data.ndwi !== null && res.data.ndwi !== undefined ? res.data.ndwi : null,
        bsi: res.data.bsi !== null && res.data.bsi !== undefined ? res.data.bsi : null,
        savi: res.data.savi !== null && res.data.savi !== undefined ? res.data.savi : null,
        ndmi: res.data.ndmi !== null && res.data.ndmi !== undefined ? res.data.ndmi : null,
        msi: res.data.msi !== null && res.data.msi !== undefined ? res.data.msi : null,
      };

      console.log("Setting soilData:", newSoilData);
      setSoilData(newSoilData);
      setData(res.data);

      // Fetch groundwater data
      const lat = postData.latitude || res.data.latitude || res.data.lat;
      const lon = postData.longitude || res.data.longitude || res.data.lon;
      const locationName = res.data.location || res.data.location_name || location;
      const stateFromData = res.data.state;
      const districtFromData = res.data.district;

      if (lat && lon) {
        fetchGroundwaterData(lat, lon, locationName, stateFromData, districtFromData);
      }

      if (postData.latitude && postData.longitude) {
        console.log("Setting marker at input coordinates:", postData.latitude, postData.longitude);
        setMapCenter([postData.latitude, postData.longitude]);
        setZoomLevel(15);
      } else if ((res.data.latitude && res.data.longitude) || (res.data.lat && res.data.lon)) {
        const lat = res.data.latitude ?? res.data.lat;
        const lon = res.data.longitude ?? res.data.lon;
        console.log("Setting marker at API coordinates:", lat, lon);
        setMapCenter([lat, lon]);
        setZoomLevel(res.data.location_type === "district" ? 9 : 7);
      } else {
        console.warn("No valid coordinates in API response");
      }
    } catch (error) {
      console.error("Error fetching analysis:", error);
      setError("❌ Time Out/Failed to analyze location. Please try again.");
    }
    setLoading(false);
  };

  // Function to calculate polygon area using the shoelace formula
  const calculatePolygonArea = (coordinates) => {
    if (!coordinates || coordinates.length < 3) return 0;

    let area = 0;
    const earthRadius = 6371; // Earth's radius in kilometers

    // Convert to radians and calculate area using spherical geometry
    const coords = coordinates.map(coord => ({
      lat: coord.lat * Math.PI / 180,
      lng: coord.lng * Math.PI / 180
    }));

    for (let i = 0; i < coords.length; i++) {
      const j = (i + 1) % coords.length;
      area += coords[i].lng * coords[j].lat;
      area -= coords[j].lng * coords[i].lat;
    }

    area = Math.abs(area) / 2;
    area = area * earthRadius * earthRadius;

    return area.toFixed(2);
  };

  // Function to calculate area for any shape type
  const calculateShapeArea = (bounds, type) => {
    if (!bounds) return 0;

    if (type === 'rectangle' && bounds.length === 2) {
      // Rectangle bounds in [[lat1, lng1], [lat2, lng2]] format
      return calculateArea(bounds);
    } else if (Array.isArray(bounds) && bounds.length > 2) {
      // Custom polygon bounds
      return calculatePolygonArea(bounds);
    }
    return 0;
  };

  // Function to analyze custom shapes
  const analyzeCustomShape = async (coordinates, type) => {
    setLoading(true);

    try {
      // Calculate center point for analysis
      const lats = coordinates.map(coord => coord.lat);
      const lngs = coordinates.map(coord => coord.lng);
      const centerLat = lats.reduce((a, b) => a + b, 0) / lats.length;
      const centerLng = lngs.reduce((a, b) => a + b, 0) / lngs.length;

      const formattedCoords = coordinates.map(coord => ({
        lat: coord.lat,
        lng: coord.lng
      }));

      const postData = {
        latitude: centerLat,
        longitude: centerLng,
        [type]: formattedCoords,
        shapeType: type
      };

      console.log(`${type} center coordinates:`, centerLat, centerLng);
      await fetchAnalysis(postData);

      if (mapRef.current) {
        // Fit map to show the entire shape
        const bounds = L.latLngBounds(coordinates);
        mapRef.current.fitBounds(bounds);
      }
    } catch (error) {
      console.error('Error analyzing custom shape:', error);
      setError("❌ Failed to analyze custom shape. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getConclusionSummary = (soilData, suitability, locationType) => {
    const { ndvi, evi, ndwi, bsi, savi, ndmi, msi } = soilData || {};
    const summary = [];

    // Check if this is urban/semi-urban area
    if (locationType === "urban" || locationType === "semi-urban") {
      summary.push("🏙️ URBAN/SEMI-URBAN AREA DETECTED");
      summary.push("\nThis area has significant built-up infrastructure. Traditional large-scale farming may not be feasible here.");
      summary.push("\n💡 ALTERNATIVE FARMING OPTIONS:");
      summary.push("• Rooftop farming - Grow vegetables like tomatoes, spinach, herbs");
      summary.push("• Container gardening - Use pots for small-scale cultivation");
      summary.push("• Vertical farming - Maximize limited space with vertical structures");
      summary.push("• Community gardens - Join or start local farming initiatives");
      return summary.join("\n");
    }

    // Calculate overall health scores
    const vegetationScore = ndvi != null ? ndvi : 0;
    const waterScore = ndwi != null ? ndwi : 0;
    const moistureStress = msi != null ? msi : 1.0;
    const soilMoisture = data?.soil_moisture != null ? data.soil_moisture * 100 : null;
    const rainfall = data?.rainfall_mm != null ? data.rainfall_mm : null;
    const temperature = data?.temperature_c != null ? data.temperature_c : null;

    // Get groundwater data
    const gwCategory = groundwaterData?.category || null;
    const gwExtraction = groundwaterData?.groundWaterExtraction || 0;
    const gwResources = groundwaterData?.annualExtractableGroundWaterResources || 0;
    const gwExtractionRate = gwResources > 0 ? (gwExtraction / gwResources) * 100 : 0;
    const gwRainfall = groundwaterData?.rainfall || null;

    // Get crop recommendations
    const recommendedCrops = data?.recommended_crops || [];
    const recommendedFertilizers = data?.recommended_fertilizers || [];

    // Calculate area if available
    const areaKm = rectangleBounds ? parseFloat(calculateArea(rectangleBounds)) : 
                   customShapeBounds ? parseFloat(calculateShapeArea(customShapeBounds, shapeType)) : null;
    const areaHectares = areaKm ? (areaKm * 100).toFixed(1) : null;

    // ============ START SUMMARY ============
    summary.push("🌾 FARMING ANALYSIS FOR YOUR LAND");
    summary.push("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");

    // Location and Area Info
    if (data?.location || data?.location_name) {
      summary.push(`\n📍 ${data.location || data.location_name}${data?.state ? `, ${data.state}` : ''}${data?.district ? ` (${data.district})` : ''}`);
    }
    if (areaHectares) {
      summary.push(`📏 Area: ${areaHectares} hectares (ha)`);
    }

    // ============ WATER SITUATION ANALYSIS ============
    summary.push("\n_______________________________________________");
    summary.push("\n💧 WATER AVAILABILITY:");
    
    if (gwCategory) {
      // Determine water risk level
      let waterRisk = "UNKNOWN";
      let waterRiskEmoji = "❓";
      
      if (gwCategory === "Over Exploited" || gwExtractionRate >= 100) {
        waterRisk = "CRITICAL";
        waterRiskEmoji = "🔴";
      } else if (gwCategory === "Critical" || gwExtractionRate >= 90) {
        waterRisk = "HIGH RISK";
        waterRiskEmoji = "🟠";
      } else if (gwCategory === "Semi Critical" || gwExtractionRate >= 70) {
        waterRisk = "MODERATE";
        waterRiskEmoji = "🟡";
      } else if (gwCategory === "Safe" || gwExtractionRate < 70) {
        waterRisk = "GOOD";
        waterRiskEmoji = "🟢";
      }

      summary.push(`${waterRiskEmoji} Status: ${waterRisk} | ${gwCategory} | ${gwExtractionRate.toFixed(0)}% extraction`);
      
      // Water-specific advice
      if (gwCategory === "Over Exploited" || gwExtractionRate >= 100) {
        summary.push("\n⚠️ CRITICAL: Groundwater extraction exceeds natural recharge!");
        if (rainfall != null && rainfall < 600) summary.push(`   Low rainfall (${rainfall}mm) worsens the situation.`);
        
        summary.push("\n🚫 AVOID: Rice, Sugarcane, Banana (1200-2500mm water need)");
        
        if (areaHectares) {
          const estimatedLoss = (parseFloat(areaHectares) * 18000).toFixed(0);
          summary.push(`💰 Rice irrigation cost: ₹${parseInt(estimatedLoss).toLocaleString('en-IN')} extra for ${areaHectares} ha`);
        }
        
        summary.push("\n✅ GROW INSTEAD: Millets/Pulses/Groundnut (300-600mm) → Save 60-70% water");
        
      } else if (gwCategory === "Critical" || gwExtractionRate >= 90) {
        summary.push("\n⚠️ HIGH STRESS: Groundwater depleting faster than recharge");
        summary.push("💡 Actions: Drip irrigation (30-50% savings) | Avoid rice/sugarcane | Rainwater harvesting");
        
      } else if (gwCategory === "Semi Critical" || gwExtractionRate >= 70) {
        summary.push("\n⚡ MODERATE: Water under pressure but manageable");
        summary.push("💡 Actions: Efficient irrigation | Monitor usage | Mix crop types");
        
      } else {
        summary.push("\n✅ GOOD: Sustainable groundwater, can grow most crops");
        if (rainfall != null && rainfall > 800) summary.push(`   Rainfall: ${rainfall}mm (Good)`);
      }
      
    } else {
      // No groundwater data available
      summary.push("⚠️ DATA NOT AVAILABLE for this location");
      
      // Use rainfall and NDWI as backup indicators
      if (rainfall != null) {
        if (rainfall < 500) {
          summary.push(`Low rainfall (${rainfall}mm) → Use drought-resistant crops & efficient irrigation`);
        } else if (rainfall > 1000) {
          summary.push(`Good rainfall (${rainfall}mm) supports crop growth`);
        }
      }
      
      if (waterScore < -0.1) {
        summary.push("Low soil water (NDWI) → Increase irrigation frequency");
      }
    }

    // ============ SOIL HEALTH ANALYSIS ============
    summary.push("\n_______________________________________________");
    summary.push("\n🌱 SOIL HEALTH:");
    
    let soilHealthStatus = "UNKNOWN";
    let soilHealthEmoji = "❓";
    
    if (vegetationScore > 0.6) {
      soilHealthStatus = "EXCELLENT";
      soilHealthEmoji = "🟢";
    } else if (vegetationScore > 0.4) {
      soilHealthStatus = "GOOD";
      soilHealthEmoji = "🟢";
    } else if (vegetationScore > 0.2) {
      soilHealthStatus = "MODERATE";
      soilHealthEmoji = "🟡";
    } else {
      soilHealthStatus = "POOR";
      soilHealthEmoji = "🔴";
    }
    
    const soilMetrics = [];
    if (ndvi != null) soilMetrics.push(`NDVI: ${ndvi.toFixed(3)}`);
    if (soilMoisture != null) soilMetrics.push(`Moisture: ${soilMoisture.toFixed(1)}%`);
    if (moistureStress != null) soilMetrics.push(`Stress: ${moistureStress.toFixed(2)}`);
    
    summary.push(`${soilHealthEmoji} Status: ${soilHealthStatus} | ${soilMetrics.join(' | ')}`);
    
    // Compact soil assessment
    const soilIssues = [];
    if (ndvi != null && ndvi < 0.3) soilIssues.push("Low vegetation");
    if (soilMoisture != null && soilMoisture < 15) soilIssues.push("Dry soil");
    if (moistureStress > 1.2) soilIssues.push("High stress");
    
    if (soilIssues.length > 0) {
      summary.push(`⚠️ Issues: ${soilIssues.join(', ')} → Needs irrigation & nutrients`);
    } else if (ndvi != null && ndvi > 0.6 && soilMoisture != null && soilMoisture > 25) {
      summary.push("✅ Excellent conditions - crops thriving with good moisture");
    }

    // ============ CROP RECOMMENDATIONS ============
    if (recommendedCrops && recommendedCrops.length > 0) {
      summary.push("\n_______________________________________________");
      summary.push("\n🌾 RECOMMENDED CROPS:");
      
      // Filter crops based on water availability
      const waterIntensiveCrops = ['rice', 'paddy', 'sugarcane', 'banana'];
      const lowWaterCrops = ['millet', 'bajra', 'jowar', 'sorghum', 'pulses', 'groundnut', 'chickpea', 'moong', 'urad'];
      
      const safeCrops = [];
      const riskyCrops = [];
      
      recommendedCrops.forEach(crop => {
        const cropLower = crop.toLowerCase();
        
        if (waterIntensiveCrops.some(wc => cropLower.includes(wc))) {
          if (gwCategory === "Over Exploited" || gwExtractionRate >= 100) {
            riskyCrops.push(`${crop} ⚠️ HIGH RISK`);
          } else if (gwCategory === "Critical" || gwExtractionRate >= 90) {
            riskyCrops.push(`${crop} ⚡ Use drip irrigation`);
          } else {
            safeCrops.push(`${crop} 💧`);
          }
        } else if (lowWaterCrops.some(lc => cropLower.includes(lc))) {
          safeCrops.push(`${crop} ✅`);
        } else {
          safeCrops.push(crop);
        }
      });
      
      if (safeCrops.length > 0) {
        summary.push(`✅ Best: ${safeCrops.join(', ')}`);
      }
      if (riskyCrops.length > 0) {
        summary.push(`⚠️ Risky: ${riskyCrops.join(', ')}`);
      }
      
      // Provide alternatives for water-stressed areas
      if ((gwCategory === "Over Exploited" || gwExtractionRate >= 100) && riskyCrops.length > 0) {
        summary.push("💡 Better: Millets, Pulses, Groundnut (60-70% less water)");
      }
    }

    // ============ FERTILIZER RECOMMENDATIONS ============
    if (recommendedFertilizers && recommendedFertilizers.length > 0) {
      summary.push("\n_______________________________________________");
      summary.push("\n🧪 FERTILIZERS: " + recommendedFertilizers.join(', '));
      
      if (soilMoisture != null && soilMoisture > 20) {
        summary.push("✅ Apply now - soil moisture optimal for absorption");
      } else if (soilMoisture != null && soilMoisture < 15) {
        summary.push("⚠️ Water field first before applying fertilizers");
      }
    }

    // ============ ECONOMIC IMPACT ============
    if (areaHectares) {
      summary.push("\n_______________________________________________");
      summary.push("\n💰 ECONOMIC IMPACT (for " + areaHectares + " ha):");
      
      if (gwCategory === "Over Exploited" || gwExtractionRate >= 100) {
        const irrigationCostPerHectare = 18000;
        const totalExtraCost = parseFloat(areaHectares) * irrigationCostPerHectare;
        const savingsWithDrip = totalExtraCost * 0.35;
        const savingsWithMiliet = totalExtraCost * 0.65;
        
        summary.push(`⚠️ Rice irrigation: ₹${Math.round(totalExtraCost).toLocaleString('en-IN')} extra cost`);
        summary.push(`✅ Drip irrigation saves: ₹${Math.round(savingsWithDrip).toLocaleString('en-IN')} (35%)`);
        summary.push(`✅ Switch to millets saves: ₹${Math.round(savingsWithMiliet).toLocaleString('en-IN')} (65%)`);
        
      } else if ((gwCategory === "Critical" || gwCategory === "Semi Critical") && gwCategory) {
        const irrigationCostPerHectare = 12000;
        const totalCost = parseFloat(areaHectares) * irrigationCostPerHectare;
        const savingsWithDrip = totalCost * 0.35;
        
        summary.push(`💧 Irrigation cost: ₹${Math.round(totalCost).toLocaleString('en-IN')}`);
        summary.push(`✅ Drip irrigation saves: ₹${Math.round(savingsWithDrip).toLocaleString('en-IN')} (35%)`);
        
      } else if (gwCategory === "Safe" && vegetationScore > 0.5) {
        summary.push("✅ Optimal conditions → Expected yield: 85-95% of maximum");
        summary.push("💡 Follow recommendations for best returns");
        
      } else if (gwCategory) {
        summary.push("💡 Good farming conditions detected");
        summary.push("✅ Follow crop & fertilizer recommendations for optimal yields");
      } else {
        // No groundwater data but still show general economic advice
        summary.push("💡 Economic impact depends on water availability");
        summary.push("✅ Use efficient irrigation to reduce costs by 30-40%");
      }
    }

    // ============ IMMEDIATE ACTION PLAN ============
    summary.push("\n_______________________________________________");
    summary.push("\n✅ ACTION PLAN:");
    
    const actions = [];
    
    // Water management actions
    if (gwCategory === "Over Exploited" || gwExtractionRate >= 100) {
      actions.push("1. URGENT: Switch to millets/pulses (avoid rice/sugarcane)");
      actions.push("2. Install drip irrigation (₹40-60k, saves 35%)");
      actions.push("3. Rainwater harvesting for groundwater recharge");
    } else if (gwCategory === "Critical" || gwCategory === "Semi Critical") {
      actions.push("1. Use drip/sprinkler irrigation (30-40% water savings)");
    }
    
    // Soil management actions
    if (vegetationScore < 0.4) {
      actions.push(`${actions.length + 1}. Add organic manure/compost for soil health`);
    }
    
    if (soilMoisture != null && soilMoisture < 20) {
      actions.push(`${actions.length + 1}. Increase irrigation - soil is dry`);
    }
    
    // Fertilizer timing
    if (recommendedFertilizers && recommendedFertilizers.length > 0 && soilMoisture != null && soilMoisture > 20) {
      actions.push(`${actions.length + 1}. Apply fertilizers now (optimal moisture)`);
    }
    
    // Seasonal planting advice
    const currentMonth = new Date().getMonth() + 1; // 1-12
    if (currentMonth >= 10 && currentMonth <= 12) {
      actions.push(`${actions.length + 1}. Rabi season: Plant wheat/chickpea in 2-4 weeks`);
    } else if (currentMonth >= 6 && currentMonth <= 8) {
      actions.push(`${actions.length + 1}. Kharif season: Prepare for monsoon crops`);
    }
    
    if (actions.length > 0) {
      summary.push(actions.join('\n'));
    } else {
      summary.push("Follow crop and fertilizer recommendations above");
    }

    // ============ FINAL SUMMARY ============
    summary.push("\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
    summary.push("🎯 KEY TAKEAWAY:");
    
    if (gwCategory === "Over Exploited" && vegetationScore > 0.5) {
      summary.push("Healthy soil BUT critical water shortage → Switch to drought-resistant crops NOW");
    } else if (gwCategory === "Safe" && vegetationScore > 0.5) {
      summary.push("Excellent conditions! Good soil + water → Follow recommendations for 85-95% yields");
    } else if (gwCategory === "Over Exploited" && vegetationScore < 0.4) {
      summary.push("Water + soil both need attention → Prioritize water conservation & soil improvement");
    } else if (vegetationScore < 0.3) {
      summary.push("Poor soil health → Add organic matter & improve irrigation immediately");
    } else if (gwCategory === "Safe") {
      summary.push("Good conditions detected → Follow recommendations for optimal results");
    } else {
      summary.push("Follow the recommendations above for best farming results");
    }

    return summary.join("\n");
  };

  useEffect(() => {
    if (mapRef.current && mapCenter && zoomLevel) {
      console.log("Updating map view to:", mapCenter, "Zoom:", zoomLevel);
      mapRef.current.setView(mapCenter, zoomLevel);
    }
  }, [mapCenter, zoomLevel]);

  useEffect(() => {
    if (latitude && longitude && !isNaN(parseFloat(latitude)) && !isNaN(parseFloat(longitude))) {
      console.log("Marker should render at input coordinates:", latitude, longitude);
    } else if (data && (data.latitude || data.lat) && (data.longitude || data.lon)) {
      console.log("Marker should render at API coordinates:", data.latitude || data.lat, data.longitude || data.lon);
    }
  }, [latitude, longitude, data]);

  const getPolygonPositions = () => {
    if (!data?.polygon) return [];
    return data.polygon.map((coord) => [coord[1], coord[0]]);
  };

  const getPolygonColor = () => {
    // if (data?.location_type === "district") {
    //   return { color: "#007bff", fillColor: "#007bff", fillOpacity: 0.0 };
    // }
    // return { color: "#28a745", fillColor: "#28a745", fillOpacity: 0.0 };
  };

  const formatRecommendations = (items) => {
    if (!items || !Array.isArray(items)) return "N/A";
    return items.filter(Boolean).join(", ") || "N/A";
  };

  // Enhanced _onCreated function to handle different shape types
  const _onCreated = async (e) => {
    console.log("Shape created event:", e);
    const { layerType, layer } = e;

    try {
      if (layerType === 'rectangle') {
        console.log("Rectangle created");
        const bounds = layer.getBounds();
        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();
        const newRectangleBounds = [
          [sw.lat, sw.lng],
          [ne.lat, ne.lng]
        ];
        setRectangleBounds(newRectangleBounds);
        setCustomShapeBounds(null);
        setShapeType('rectangle');
        setHasDrawnShape(true);

        const lat = (sw.lat + ne.lat) / 2;
        const lon = (sw.lng + ne.lng) / 2;

        const postData = {
          latitude: lat,
          longitude: lon,
          rectangle: newRectangleBounds,
        };
        console.log("Rectangle center coordinates:", lat, lon);
        await fetchAnalysis(postData);

        if (mapRef.current) {
          mapRef.current.fitBounds(bounds);
        }
      }
      else if (layerType === 'polygon') {
        console.log("Polygon created, layer:", layer);

        // Get the coordinates properly
        let latlngs;
        if (layer.getLatLngs && typeof layer.getLatLngs === 'function') {
          const coords = layer.getLatLngs();
          console.log("Raw getLatLngs result:", coords);

          // Handle nested array structure that Leaflet sometimes returns
          if (Array.isArray(coords) && coords.length > 0) {
            // If it's nested (coords[0] is an array), use the first array
            latlngs = Array.isArray(coords[0]) ? coords[0] : coords;
          } else {
            console.error("Unexpected coordinate structure:", coords);
            return;
          }
        } else {
          console.error("Layer does not have getLatLngs method");
          return;
        }

        console.log("Processed polygon coordinates:", latlngs);

        if (!latlngs || latlngs.length < 3) {
          console.error("Invalid polygon coordinates - need at least 3 points");
          return;
        }

        setCustomShapeBounds(latlngs);
        setRectangleBounds(null);
        setShapeType('polygon');
        setHasDrawnShape(true);

        // Analyze the custom polygon
        await analyzeCustomShape(latlngs, 'polygon');
      } else {
        console.log("Unknown layer type:", layerType);
      }
    } catch (error) {
      console.error("Error in _onCreated:", error);
      setError("Failed to process the drawn shape. Please try again.");
    }
  };

  const _onDeleted = () => {
    console.log("Shape deleted");
    setRectangleBounds(null);
    setCustomShapeBounds(null);
    setShapeType(null);
    setData(null);
    setSoilData(null);
    setError(null);
    setHasDrawnShape(false);
    if (mapRef.current) {
      setMapCenter([20.5937, 78.9629]);
      setZoomLevel(5);
      mapRef.current.setView([20.5937, 78.9629], 5);
    }
  };

  // Reset to India function - clears all shapes and resets map view
  const handleResetToIndia = () => {
    console.log("Resetting to India view");
    
    // Clear all drawn shapes from the feature group
    if (featureGroupRef.current) {
      featureGroupRef.current.clearLayers();
    }
    
    // Reset all state variables
    setRectangleBounds(null);
    setCustomShapeBounds(null);
    setShapeType(null);
    setData(null);
    setSoilData(null);
    setGroundwaterData(null);
    setError(null);
    setHasDrawnShape(false);
    setLocation("");
    setPincode("");
    setLatitude("");
    setLongitude("");
    
    // Reset map view to India
    const indiaCenter = [20.5937, 78.9629];
    const indiaZoom = 5;
    setMapCenter(indiaCenter);
    setZoomLevel(indiaZoom);
    
    if (mapRef.current) {
      mapRef.current.setView(indiaCenter, indiaZoom);
    }
  };

  const _onEdited = (e) => {
    console.log("Shape edited:", e);
    // Handle edited shapes if needed
  };

  // Handler for freehand shape creation
  const handleFreehandShapeCreated = (coordinates, type) => {
    setCustomShapeBounds(coordinates);
    setRectangleBounds(null);
    setShapeType(type);
    setHasDrawnShape(true);
    analyzeCustomShape(coordinates, type);
  };

  return (
    <div className="india-map-container">
      <div className="header-section">
        <h1 className="header-title">
          <FaSeedling /> Indian Crop Advisory
        </h1>
        <p className="header-subtitle">
          {isStateOrDistrictUser() ? (
            <>Get <strong>real-time agricultural recommendations</strong> for your {window.location.pathname.includes('/state') ? 'state' : 'district'}. Draw shapes on the map to analyze specific areas and get detailed crop and soil insights.</>
          ) : (
            <>Get <strong>real-time agricultural recommendations</strong> based on your state, district, or custom area. Make better crop decisions with weather, soil, and expert advice.</>
          )}
        </p>
      </div>

      <div className="steps-section" onClick={() => setShowSteps(!showSteps)}>
        <h3 className="steps-title">How It Works</h3>
        {showSteps && (
          <ol className="steps-list">
            {!isStateOrDistrictUser() && (
              <li>Enter a State, District, or Latitude/Longitude.</li>
            )}
            <li>{isStateOrDistrictUser() ? 'Draw' : 'For a more detailed analysis, draw'} a shape on the map using:</li>
            <ul style={{ marginLeft: '20px', marginTop: '5px' }}>
              <li><strong>Rectangle tool:</strong> Draw rectangular areas</li>
              <li><strong>Polygon tool:</strong> Click multiple points to create custom polygons</li>
              <li><strong>Freehand tool:</strong> Click and drag to draw any custom shape like Google Earth</li>
            </ul>
            <li>The system fetches real-time weather and soil data for the selected area.</li>
            <li>Data is analyzed to recommend suitable crops and fertilizers.</li>
            <li>View detailed recommendations and visualize the area on the map.</li>
          </ol>
        )}
      </div>

      {/* Input section - only show for farmers, hide for state/district users */}
      {!isStateOrDistrictUser() && (
        <div className="input-section">
          <h3 className="input-title">Enter a State, District, or Latitude/Longitude</h3>
          <form onSubmit={handleSubmit} className="input-form">
            <input
              type="text"
              value={location}
              onChange={handleInputChange}
              placeholder="e.g., Punjab, Ludhiana"
              className="input-field location-input"
              onFocus={(e) => e.target.classList.add("focused")}
              onBlur={(e) => e.target.classList.remove("focused")}
            />
            <input
              type="text"
              value={pincode}
              onChange={handlePincodeChange}
              placeholder="Pincode (e.g., 110001)"
              className="input-field pincode-input"
              onFocus={(e) => e.target.classList.add("focused")}
              onBlur={(e) => e.target.classList.remove("focused")}
            />
            <input
              type="text"
              value={latitude}
              onChange={handleLatitudeChange}
              placeholder="Latitude (e.g., 28.5388)"
              className="input-field latitude-input"
              onFocus={(e) => e.target.classList.add("focused")}
              onBlur={(e) => e.target.classList.remove("focused")}
            />
            <input
              type="text"
              value={longitude}
              onChange={handleLongitudeChange}
              placeholder="Longitude (e.g., 77.1551)"
              className="input-field longitude-input"
              onFocus={(e) => e.target.classList.add("focused")}
              onBlur={(e) => e.target.classList.remove("focused")}
            />
            <button
              type="submit"
              disabled={loading}
              className={`submit-button ${loading ? "disabled" : ""}`}
            >
              {loading ? "🔄 Analyzing..." : "Analyze"}
            </button>
          </form>
          {error && (
            <div className="error-message">{error}</div>
          )}
        </div>
      )}

      <div className="map-section">
        <h3 className="map-title">🗺 Location Map</h3>
        <MapContainer
          center={mapCenter}
          zoom={zoomLevel}
          className="map-container"
          ref={mapRef}
          zoomControl={true}
          scrollWheelZoom={true}
        >
          <LayersControl position="topright">
            <LayersControl.BaseLayer checked name="Satellite View">
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                maxZoom={28}
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Street Map">
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                maxZoom={18}
              />
            </LayersControl.BaseLayer>
            <LayersControl.Overlay checked name="Place Labels">
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                attribution='© <a href="https://www.esri.com/">Esri</a>'
                maxZoom={18}
              />
            </LayersControl.Overlay>
          </LayersControl>

          <FeatureGroup ref={featureGroupRef}>
            <EditControl
              position="topright"
              onCreated={_onCreated}
              onDeleted={_onDeleted}
              onEdited={_onEdited}
              draw={{
                rectangle: {
                  shapeOptions: {
                    color: "#ff7800",
                    weight: 2,
                    fillOpacity: 0.2
                  }
                },
                polygon: {
                  shapeOptions: {
                    color: "#2196F3",
                    weight: 2,
                    fillOpacity: 0.2
                  },
                  allowIntersection: false,
                  drawError: {
                    color: '#e1e100',
                    message: '<strong>Error:</strong> Shape edges cannot cross!'
                  },
                  showArea: true
                },
                circle: false,
                circlemarker: false,
                marker: false,
                polyline: false,
              }}
              edit={{
                edit: true,
                remove: true,
                poly: {
                  allowIntersection: false
                }
              }}
            />

            {/* Existing marker logic */}
            {(latitude && longitude && !isNaN(parseFloat(latitude)) && !isNaN(parseFloat(longitude))) ? (
              <Marker position={[parseFloat(latitude), parseFloat(longitude)]}>
                <Popup>
                  <strong>{data?.location || location || "Entered Location"}</strong><br />
                  📍 {parseFloat(latitude).toFixed(4)}, {parseFloat(longitude).toFixed(4)}
                </Popup>
              </Marker>
            ) : (
              data && (data.latitude || data.lat) && (data.longitude || data.lon) && (
                <Marker position={[data.latitude || data.lat, data.longitude || data.lon]}>
                  <Popup>
                    <strong>{data.location || location}</strong><br />
                    📍 {(data.latitude || data.lat).toFixed(4)}, {(data.longitude || data.lon).toFixed(4)}
                  </Popup>
                </Marker>
              )
            )}
          </FeatureGroup>

          {/* Existing circle and polygon logic */}
          {data && (data.latitude || data.lat) && (data.longitude || data.lon) && (
            <>
              <Circle
                center={[data.latitude || data.lat, data.longitude || data.lon]}
              >
                <Popup>
                  <strong>{data.location || location}</strong><br />
                  📍 {(data.latitude || data.lat).toFixed(4)}, {(data.longitude || data.lon).toFixed(4)}
                </Popup>
              </Circle>
              {data.polygon && (
                <Polygon positions={getPolygonPositions()} pathOptions={getPolygonColor()} />
              )}
            </>
          )}

          {/* Add custom freehand drawing component */}
          <FreehandDrawing
            onShapeCreated={handleFreehandShapeCreated}
            featureGroupRef={featureGroupRef}
          />

          {/* Reset to India Button */}
          <div style={{
            position: 'absolute',
            top: '70px',
            left: '10px',
            zIndex: 1000,
            background: 'white',
            padding: '8px',
            borderRadius: '6px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
            border: '2px solid #ddd'
          }}>
            <button
              onClick={handleResetToIndia}
              style={{
                background: '#28a745',
                color: '#fff',
                border: '2px solid #28a745',
                padding: '8px 14px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#218838';
                e.target.style.borderColor = '#218838';
                e.target.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#28a745';
                e.target.style.borderColor = '#28a745';
                e.target.style.transform = 'scale(1)';
              }}
              title="Reset map to India view and clear all shapes"
            >
              🔄 Reset to India
            </button>
          </div>
        </MapContainer>

        {/* Display area calculation for any drawn shape */}
        {(rectangleBounds || customShapeBounds) && (
          <div className="area-info">
            Total Area: {
              (() => {
                const areaKm = rectangleBounds ?
                  parseFloat(calculateArea(rectangleBounds)) :
                  parseFloat(calculateShapeArea(customShapeBounds, shapeType));
                const areaHa = (areaKm * 100).toFixed(2);
                return `${areaHa} hectares (ha)`;
              })()
            }
            <br />
            Shape Type: {shapeType === 'rectangle' ? 'Rectangle' :
              shapeType === 'polygon' ? 'Custom Polygon' :
                shapeType === 'freehand' ? 'Freehand Shape' : 'Custom Shape'}
          </div>
        )}
      </div>

      <div className="data-section">
        {loading && (
          <div className="loading-message">
            <p>🔄 Analyzing area...</p>
          </div>
        )}
        {data && !loading && (
          <>
            {!hasDrawnShape && (
              <div className="draw-shape-message">
                Please draw a shape on the map to view more precise details including soil analysis, recommendations, and detailed reports. You can use:
                <ul style={{ marginLeft: '20px', marginTop: '10px' }}>
                  <li><strong>Rectangle Tool:</strong> Draw rectangular areas (orange)</li>
                  <li><strong>Polygon Tool:</strong> Click multiple points for custom shapes (blue)</li>
                  <li><strong>Freehand Tool:</strong> Click and drag for any custom shape (purple)</li>
                </ul>
              </div>
            )}

            {/* 3-Column Grid Layout */}
            <div className="data-grid-3col">
              {/* Column 1: Location Details */}
              <div
                data-section="location-details"
                className="data-card grid-location"
              >
                <h3 className="card-title">📍 Location Details</h3>
                <p><FaMapMarkerAlt color="#28a745" /> <strong>Location:</strong> {data.location || data.location_name || location || "N/A"}</p>
                <p><strong>Type:</strong>
                  <span className={`type-badge ${data.location_type || ''}`}>
                    {data.location_type || "N/A"}
                  </span>
                </p>
                {data.state && <p><strong>State:</strong> {data.state}</p>}
                {data.district && <p><strong>District:</strong> {data.district}</p>}
                {data.city && <p><strong>City:</strong> {data.city}</p>}
                <p><strong>Coordinates:</strong> {(data.latitude || data.lat)?.toFixed(4) || "N/A"}, {(data.longitude || data.lon)?.toFixed(4) || "N/A"}</p>

                {/* Debug info for groundwater matching */}
                {(data.state || data.district) && (
                  <div style={{ marginTop: '10px', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px', fontSize: '11px', color: '#666' }}>
                    <strong>For Groundwater Data:</strong>
                    {data.state && ` State: "${data.state}"`}
                    {data.district && ` District: "${data.district}"`}
                  </div>
                )}
              </div>

              {/* Column 1: Weather & Soil Data */}
              <div
                data-section="weather-soil-data"
                className="data-card grid-weather"
              >
                <h3 className="card-title">🌤 Weather & Soil Data</h3>
                <p><FaCloudRain color="#17a2b8" /> <strong>Rainfall:</strong> {data.rainfall_mm != null ? `${data.rainfall_mm} mm` : "N/A"}</p>
                <p><FaTemperatureHigh color="#fd7e14" /> <strong>Temperature:</strong> {data.temperature_c != null ? `${data.temperature_c}°C` : "N/A"}</p>
                <p><FaTint color="#6f42c1" /> <strong>Soil Moisture:</strong> {data.soil_moisture != null ? `${(data.soil_moisture * 100).toFixed(1)}%` : "N/A"}</p>
                <p><FaLeaf color="#28a745" /> <strong>Soil Suitability:</strong> {data.suitability || "N/A"}</p>
              </div>

              {/* Column 2: Recommendations (spans both rows for balance) */}
              {hasDrawnShape && (
                <div
                  data-section="recommendations"
                  className="data-card grid-recommendations"
                >
                  <h3 className="card-title">🌱 Recommendations</h3>
                  <p><FaSeedling color="#28a745" /> <strong>Crops:</strong> {formatRecommendations(data.recommended_crops)}</p>
                  <p><FaFlask color="#dc3545" /> <strong>Fertilizers:</strong> {formatRecommendations(data.recommended_fertilizers)}</p>
                </div>
              )}

              {/* Column 3: Groundwater Resources (spans full height) */}
              {hasDrawnShape && (
                <div
                  data-section="groundwater-data"
                  className="data-card groundwater-card grid-groundwater"
                >
                  <h3 className="card-title">💧 Groundwater Resources</h3>
                  {loadingGroundwater ? (
                    <div className="loading-groundwater">
                      <p>🔄 Loading groundwater data...</p>
                    </div>
                  ) : groundwaterData ? (
                    <div className="groundwater-content">
                      <div className="groundwater-header">
                        <h4>{groundwaterData.name || groundwaterData.location || 'Region'}</h4>
                        {groundwaterData.category && (
                          <span
                            className="gw-category-badge"
                            style={{
                              backgroundColor:
                                groundwaterData.category === 'Safe' ? '#1fc21f' :
                                  groundwaterData.category === 'Semi Critical' ? '#f9c802' :
                                    groundwaterData.category === 'Critical' ? '#ff8d1a' :
                                      groundwaterData.category === 'Over Exploited' ? '#ea2328' :
                                        '#e6e6e6',
                              color: '#fff',
                              padding: '2px 4px',
                              borderRadius: '5px',
                              fontSize: '13px',
                              fontWeight: 'bold',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {groundwaterData.category}
                          </span>
                        )}
                      </div>

                      <div className="groundwater-metrics">
                        <div className="gw-metric-item">
                          <div className="gw-metric-icon">🌧️</div>
                          <div className="gw-metric-content">
                            <span className="gw-metric-label">Annual Rainfall</span>
                            <span className="gw-metric-value">
                              {groundwaterData.rainfall?.toFixed(2) || 'N/A'} mm
                            </span>
                          </div>
                        </div>

                        <div className="gw-metric-item">
                          <div className="gw-metric-icon">💧</div>
                          <div className="gw-metric-content">
                            <span className="gw-metric-label">Extractable Resources</span>
                            <span className="gw-metric-value">
                              {groundwaterData.annualExtractableGroundWaterResources?.toFixed(2) || 'N/A'} ham
                            </span>
                          </div>
                        </div>

                        <div className="gw-metric-item">
                          <div className="gw-metric-icon">⚡</div>
                          <div className="gw-metric-content">
                            <span className="gw-metric-label">Current Extraction</span>
                            <span className="gw-metric-value">
                              {groundwaterData.groundWaterExtraction?.toFixed(2) || 'N/A'} ham
                            </span>
                          </div>
                        </div>

                        <div className="gw-metric-item">
                          <div className="gw-metric-icon">📊</div>
                          <div className="gw-metric-content">
                            <span className="gw-metric-label">Extraction Rate</span>
                            <span className="gw-metric-value" style={{
                              color:
                                groundwaterData.annualExtractableGroundWaterResources > 0 ?
                                  ((groundwaterData.groundWaterExtraction / groundwaterData.annualExtractableGroundWaterResources) * 100) >= 100 ? '#ea2328' :
                                    ((groundwaterData.groundWaterExtraction / groundwaterData.annualExtractableGroundWaterResources) * 100) >= 90 ? '#ff8d1a' :
                                      ((groundwaterData.groundWaterExtraction / groundwaterData.annualExtractableGroundWaterResources) * 100) >= 70 ? '#f9c802' :
                                        '#1fc21f'
                                  : '#666'
                            }}>
                              {groundwaterData.annualExtractableGroundWaterResources > 0
                                ? ((groundwaterData.groundWaterExtraction / groundwaterData.annualExtractableGroundWaterResources) * 100).toFixed(1)
                                : 'N/A'}%
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="groundwater-insight">
                        <h5>💡 Water Management Insight</h5>
                        <p>
                          {groundwaterData.annualExtractableGroundWaterResources > 0 ? (
                            ((groundwaterData.groundWaterExtraction / groundwaterData.annualExtractableGroundWaterResources) * 100) >= 100 ? (
                              <>
                                <strong style={{ color: '#ea2328' }}>⚠️ Critical:</strong> This area is extracting more groundwater than can be naturally replenished.
                                Immediate water conservation measures are essential. Consider rainwater harvesting and drip irrigation to reduce groundwater dependency.
                              </>
                            ) : ((groundwaterData.groundWaterExtraction / groundwaterData.annualExtractableGroundWaterResources) * 100) >= 90 ? (
                              <>
                                <strong style={{ color: '#ff8d1a' }}>⚠️ High Risk:</strong> Groundwater extraction is approaching critical levels.
                                Implement water-saving irrigation methods and monitor usage closely to prevent depletion.
                              </>
                            ) : ((groundwaterData.groundWaterExtraction / groundwaterData.annualExtractableGroundWaterResources) * 100) >= 70 ? (
                              <>
                                <strong style={{ color: '#f9c802' }}>⚡ Moderate:</strong> Groundwater usage is significant.
                                Consider water conservation practices and efficient irrigation systems to maintain sustainability.
                              </>
                            ) : (
                              <>
                                <strong style={{ color: '#1fc21f' }}>✅ Sustainable:</strong> Groundwater resources are being managed well.
                                Continue current practices and monitor regularly to maintain this healthy balance.
                              </>
                            )
                          ) : (
                            'Groundwater data is being analyzed for this region.'
                          )}
                        </p>
                      </div>

                      {/* {groundwaterData.districts && groundwaterData.districts.length > 0 && (
                        <div className="groundwater-districts">
                          <h5>📍 District-wise Data Available</h5>
                          <p style={{ fontSize: '14px', color: '#666' }}>
                            This state has detailed data for {groundwaterData.districts.length} districts.
                            Select a specific district for more precise groundwater information.
                          </p>
                        </div>
                      )} */}

                      {/* Data Source Attribution */}
                      <div style={{ 
                        marginTop: '15px', 
                        paddingTop: '12px', 
                        borderTop: '1px solid rgba(33, 150, 243, 0.2)',
                        fontSize: '11px',
                        color: '#666',
                        fontStyle: 'italic',
                        textAlign: 'center'
                      }}>
                        <strong>Data Source:</strong> Central Ground Water Board, Department of WR, RD & GR, Ministry of Jal Shakti, Government of India
                      </div>
                    </div>
                  ) : (
                    <div className="no-groundwater-data">
                      <p>💧 Groundwater data not available for this specific location.</p>
                      <p style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
                        Try selecting a state or district from the dropdown, or the system will show regional data when available.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {hasDrawnShape && (
              <div className="soil-analysis-section">
                <h3 className="card-title">🛰️ Satellite Soil Analysis</h3>
                <div className="soil-analysis-grid">
                  <div className="soil-analysis-item">
                    <FaLeaf color="#28a745" size="3em" />
                    <h4>NDVI (Normalized Difference Vegetation Index)</h4>
                    <p className="soil-value">
                      {soilData && soilData.ndvi !== null && soilData.ndvi !== undefined ? soilData.ndvi.toFixed(3) : "N/A"}
                    </p>
                    <p className="soil-description">
                      Measures the density and health of green vegetation. Higher values (0.6-1.0) indicate healthy crops, while lower values (&lt;0.3) suggest sparse or stressed vegetation.
                    </p>
                  </div>
                  <div className="soil-analysis-item">
                    <FaSeedling color="#28a745" size="3em" />
                    <h4>EVI (Enhanced Vegetation Index)</h4>
                    <p className="soil-value">
                      {soilData && soilData.evi !== null && soilData.evi !== undefined ? soilData.evi.toFixed(3) : "N/A"}
                    </p>
                    <p className="soil-description">
                      More sensitive to dense vegetation and canopy structure. Values {'>'}0.5 indicate robust plant health, while values {'<'}0.2 suggest stress.
                    </p>
                  </div>
                  <div className="soil-analysis-item">
                    <FaTint color="#17a2b8" size="3em" />
                    <h4>NDWI (Normalized Difference Water Index)</h4>
                    <p className="soil-value">
                      {soilData && soilData.ndwi !== null && soilData.ndwi !== undefined ? soilData.ndwi.toFixed(3) : "N/A"}
                    </p>
                    <p className="soil-description">
                      Indicates water content in soil and vegetation. Positive values {'>'}0.1 suggest good moisture, while negative values {'<'}-0.1 indicate dryness.
                    </p>
                  </div>
                  <div className="soil-analysis-item">
                    <div className="icon-circle">
                      <FaFlask color="white" size="1.5em" />
                    </div>
                    <h4>BSI (Bare Soil Index)</h4>
                    <p className="soil-value">
                      {soilData && soilData.bsi !== null && soilData.bsi !== undefined ? soilData.bsi.toFixed(3) : "N/A"}
                    </p>
                    <p className="soil-description">
                      Highlights bare soil areas. High values {'>'}0.2 indicate exposed soil needing cultivation, while low values {'<'}0.1 suggest good vegetation cover.
                    </p>
                  </div>
                  <div className="soil-analysis-item">
                    <FaLeaf color="#28a745" size="3em" />
                    <h4>SAVI (Soil-Adjusted Vegetation Index)</h4>
                    <p className="soil-value">
                      {soilData && soilData.savi !== null && soilData.savi !== undefined ? soilData.savi.toFixed(3) : "N/A"}
                    </p>
                    <p className="soil-description">
                      Adjusts for soil brightness to measure vegetation health. Values {'>'}0.5 indicate strong vegetation, while {'<'}0.2 suggest sparse cover or soil influence.
                    </p>
                  </div>
                  <div className="soil-analysis-item">
                    <FaTint color="#17a2b8" size="3em" />
                    <h4>NDMI (Normalized Difference Moisture Index)</h4>
                    <p className="soil-value">
                      {soilData && soilData.ndmi !== null && soilData.ndmi !== undefined ? soilData.ndmi.toFixed(3) : "N/A"}
                    </p>
                    <p className="soil-description">
                      Measures moisture in vegetation. Positive values {'>'}0.1 indicate good moisture, while negative values {'<'}-0.1 suggest water stress in plants.
                    </p>
                  </div>
                  <div className="soil-analysis-item">
                    <div className="icon-circle red">
                      <FaFlask color="white" size="1.5em" />
                    </div>
                    <h4>MSI (Moisture Stress Index)</h4>
                    <p className="soil-value">
                      {soilData && soilData.msi !== null && soilData.msi !== undefined ? soilData.msi.toFixed(3) : "N/A"}
                    </p>
                    <p className="soil-description">
                      Indicates water stress in vegetation. Low values {'<'}0.8 suggest good water availability, while high values {'>'}1.2 indicate moisture stress.
                    </p>
                  </div>
                </div>
                {soilData && (
                  <div className="conclusion-section">
                    <h4 className="conclusion-title">
                      <FaLightbulb color="#ffc107" size="1.8em" /> Farming Advisory & Action Plan
                    </h4>
                    <div className="conclusion-content" style={{ whiteSpace: 'pre-line', lineHeight: '1.6' }}>
                      {getConclusionSummary(soilData, data?.suitability, data?.location_type).split('\n').map((line, idx) => (
                        <p key={idx} style={{ margin: '4px 0' }}>{line}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {hasDrawnShape && data && soilData && (
              <div className="pdf-export-section">
                <Detailspdf
                  data={data}
                  soilData={soilData}
                  hasDrawnShape={hasDrawnShape}
                  rectangleBounds={rectangleBounds}
                  customShapeBounds={customShapeBounds}
                  shapeType={shapeType}
                />
              </div>
            )}

            {hasDrawnShape && (
              <div
                data-section="weather-soil-chart"
                className="chart-section"
              >
                <h3 className="card-title">📊 Detailed Crop Analysis Statistics</h3>
                <div className="chart-container">
                  <div className="chart-card vegetation-chart">
                    <h4 className="chart-title">Vegetation & Soil Indices</h4>
                    {soilData ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <LineChart
                          data={[
                            { name: "NDVI", value: soilData.ndvi !== null && soilData.ndvi !== undefined ? soilData.ndvi : 0 },
                            { name: "EVI", value: soilData.evi !== null && soilData.evi !== undefined ? soilData.evi : 0 },
                            { name: "NDWI", value: soilData.ndwi !== null && soilData.ndwi !== undefined ? soilData.ndwi : 0 },
                            { name: "BSI", value: soilData.bsi !== null && soilData.bsi !== undefined ? soilData.bsi : 0 },
                            { name: "SAVI", value: soilData.savi !== null && soilData.savi !== undefined ? soilData.savi : 0 },
                            { name: "NDMI", value: soilData.ndmi !== null && soilData.ndmi !== undefined ? soilData.ndmi : 0 },
                            { name: "MSI", value: soilData.msi !== null && soilData.msi !== undefined ? soilData.msi : 0 },
                          ]}
                          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        >
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line type="monotone" dataKey="value" stroke="#0088FE" activeDot={{ r: 8 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="no-data">No vegetation and soil indices data available.</p>
                    )}
                  </div>
                  <div className="chart-card weather-chart">
                    <h4 className="chart-title">Weather & Soil Data</h4>
                    {data ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={[
                          { name: "Rainfall (mm)", value: data.rainfall_mm || 0 },
                          { name: "Temperature (°C)", value: data.temperature_c || 0 },
                          { name: "Soil Moisture (%)", value: data.soil_moisture != null ? (data.soil_moisture * 100) : 0 },
                          { name: "Soil Suitability", value: data.suitability === "High" ? 3 : data.suitability === "Moderate" ? 2 : 1 },
                        ]} margin={{ top: 10, right: 30, left: 20, bottom: 10 }}>
                          <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#1b3a1b" }} interval={0} angle={-30} textAnchor="end" />
                          <YAxis tick={{ fontSize: 12, fill: "#1b3a1b" }} />
                          <Tooltip />
                          <Legend verticalAlign="bottom" height={36} wrapperStyle={{ fontSize: "0.8rem" }} />
                          <Bar dataKey="value" fill="#28a745" radius={[5, 5, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="no-data">No weather and soil data available.</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="insights-section">
        <h3 className="insights-title">🌾 Agricultural Insights</h3>
        <ul className="insights-list">
          <li>India is the world's second-largest producer of wheat and rice.</li>
          <li>Monsoon rainfall directly impacts 60% of India's agriculture yield.</li>
          <li>Soil moisture above 25% is ideal for most cereal crops.</li>
          <li>Balanced fertilizer usage improves yield by 15-25%.</li>
          <li>Freehand drawing allows precise field boundary mapping for targeted analysis.</li>
        </ul>
      </div>
    </div>
  );
};

function calculateArea(bounds) {
  const R = 6371;
  const lat1 = bounds[0][0] * (Math.PI / 180);
  const lat2 = bounds[1][0] * (Math.PI / 180);
  const lon1 = bounds[0][1] * (Math.PI / 180);
  const lon2 = bounds[1][1] * (Math.PI / 180);

  const dLat = Math.abs(lat2 - lat1);
  const dLon = Math.abs(lon2 - lon1);

  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distanceLat = R * dLat;
  const distanceLon = R * dLon * Math.cos((lat1 + lat2) / 2);

  const area = distanceLat * distanceLon;
  return area.toFixed(2);
}

export default IndiaMap;